// ============================================================================
// USER MANAGER SERVICE
// ============================================================================

import { API_BASE_URL } from '@types';
import type { Users } from '../types/database';

// ============================================================================
// TYPES
// ============================================================================

export interface UserProduct {
  id: number;
  name: string;
  email: string;
  type: string;
  [key: string]: unknown;
}

export interface MappedQuizAnswer {
  question_id?: number;
  question: string;
  given_answer?: string | null;
  given_answer_numeric?: number | string;
  correct_answer?: number | string;
  correct_answer_text?: string | null;
  is_correct?: boolean;
}

export interface QuizAttempt {
  attempt: number;
  field?: string;
  answers: MappedQuizAnswer[];
}

export interface CourseReview {
  relevance?: string | number | null;
  quality?: string | number | null;
  utility?: string | number | null;
  time?: string | number | null;
  sponsor?: string | number | null;
  notes?: string | null;
}

export interface UserCourse {
  courseId: number;
  courseName: string;
  current_section: number;
  current_time: number;
  completed_at?: string | null;
  passed_at?: string | null;
  has_review?: boolean;
  review?: CourseReview | string | null;
  progress_percentage?: number;
  total_chapters?: number;
  answer_1?: unknown;
  answer_2?: unknown;
  answer_3?: unknown;
  answer_4?: unknown;
  answer_5?: unknown;
  mappedQuestionsAnswers?: MappedQuizAnswer[];
  quiz_attempts?: QuizAttempt[];
  [key: string]: unknown;
}

/**
 * UserInfo extends Users from database with additional fields
 * from API responses (permissions, roles, etc.)
 * Uses index signature to allow dynamic properties from API
 */
export interface UserInfo extends Users {
  // Additional fields not in Users database table
  // These come from API JOINs (roles/permissions from UserRoles/UserPermissions tables)
  permissions?: string[];
  roles?: string[];
  // Allow dynamic properties from API responses
  [key: string]: unknown;
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
  success?: boolean;
}

export interface UserSubscriptionResponse {
  active: ActiveSubscription[];
  unactive: InactiveSubscription[];
}

export interface ActiveSubscription {
  user_product_id: number;
  user_id: number;
  product_id: number;
  until_date: string | null;
  name: string;
  slug: string | null;
  short_description: string | null;
  image: string | null;
  category: string | null;
  sub_category: string | null;
  lang: string | null;
}

export interface InactiveSubscription {
  product_id: number;
  name: string;
  slug: string | null;
  short_description: string | null;
  image: string | null;
  category: string | null;
  sub_category: string | null;
  lang: string | null;
}

export interface AdminUserSubscription {
  product_id: number;
  name: string;
  category: string | null;
  sub_category: string | null;
  image: string | null;
  short_description: string | null;
  start_date: string | null;
  until_date: string | null;
  status: 'active' | 'expired';
}

export interface AdminUserGeolocation {
  label: string;
  address: string | null;
  region: string | null;
  ip_address: string | null;
}

export interface AdminUserRecord extends Users {
  subscriptions?: AdminUserSubscription[];
  geolocation?: AdminUserGeolocation | null;
  devices_count?: number;
  courses_count?: number;
  [key: string]: unknown;
}

export interface AdminUsersResponse {
  users: AdminUserRecord[];
  pagination: {
    currentPage: number;
    pageSize: number;
    totalRecords: number;
    totalPages: number;
  };
}

export interface SpecialLoginResponse {
  authenticated: boolean;
  message: string;
  sessionId: string;
  userId: string;
  email: string;
}

/** Fields accepted by PATCH /services/v1/admin/users/:id */
export interface UpdateUserPayload {
  email?: string;
  type?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  region?: string | null;
  is_public?: number | null;
  website?: string | null;
  role?: string | null;
  title?: string | null;
  profession?: number | null;
  contract?: string | null;
  disciple?: number | null;
  order_of_psychologists?: string | null;
  order_number?: string | null;
  profile_photo_path?: string | null;
  fiscal_code?: string | null;
  address?: string | null;
  sdi_number?: string | null;
  vat_number?: string | null;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Fetch user information from /services/me endpoint
 */
export const fetchUserInfo = async (): Promise<UserInfo | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/services/me`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 403) {
      // Not logged in
      return null;
    } else if (response.status === 400) {
      // Bad Request - treat as not logged in
      console.warn(
        '⚠️ Bad Request from /services/me - treating as not logged in'
      );
      return null;
    } else if (response.ok) {
      try {
        const responseData: ApiResponse<UserInfo> = await response.json();
        return responseData?.data || null;
      } catch (jsonError) {
        console.error('❌ Error parsing JSON response:', jsonError);
        throw new Error('Failed to parse user data');
      }
    } else {
      // Other errors
      console.warn(
        `⚠️ Unexpected status ${response.status} from /services/me - treating as not logged in`
      );
      return null;
    }
  } catch (error) {
    console.error('❌ Error fetching user info:', error);
    throw error;
  }
};

/**
 * Fetch user subscriptions
 */
export const fetchUserSubscriptions =
  async (): Promise<UserSubscriptionResponse> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/services/v1/user/subscription`,
        {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const responseData: ApiResponse<UserSubscriptionResponse> =
        await response.json();
      return responseData?.data ?? { active: [], unactive: [] };
    } catch (error) {
      console.error('❌ Error fetching user subscriptions:', error);
      throw error;
    }
  };

/**
 * Fetch specific user information from admin endpoint
 */
export const fetchUserInfoById = async (
  userId: string | number
): Promise<UserInfo | null> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/services/admin/users/${userId}`,
      {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const responseData: ApiResponse<UserInfo> = await response.json();
    return responseData?.data || null;
  } catch (error) {
    console.error('❌ Error fetching user info:', error);
    throw error;
  }
};

/**
 * Fetch user products from admin endpoint
 */
export const fetchUserProducts = async (
  userId: string | number
): Promise<UserProduct[]> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/services/admin/users/${userId}/products`,
      {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const responseData: ApiResponse<UserProduct[]> = await response.json();
    return responseData?.data || [];
  } catch (error) {
    console.error('❌ Error fetching user products:', error);
    throw error;
  }
};

/**
 * Fetch user courses from admin endpoint
 */
export const fetchUserCourses = async (
  userId: string | number
): Promise<UserCourse[]> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/services/admin/users/${userId}/courses`,
      {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const responseData: ApiResponse<UserCourse[]> = await response.json();
    return responseData?.data || [];
  } catch (error) {
    console.error('❌ Error fetching user courses:', error);
    throw error;
  }
};

/**
 * Update user information via PATCH /services/v1/admin/users/:id
 * Response body is the updated user object (not wrapped in { data }).
 */
export const updateUserInfo = async (
  userId: string | number,
  userData: UpdateUserPayload
): Promise<UserInfo> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/services/v1/admin/users/${userId}`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const responseData: UserInfo | ApiResponse<UserInfo> =
      await response.json();
    if (
      responseData &&
      typeof responseData === 'object' &&
      'data' in responseData
    ) {
      return (responseData as ApiResponse<UserInfo>).data;
    }
    return responseData as UserInfo;
  } catch (error) {
    console.error('❌ Error updating user info:', error);
    throw error;
  }
};

/**
 * Delete user via DELETE /services/v1/admin/users/:id (hard delete)
 */
export const deleteUser = async (userId: string | number): Promise<boolean> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/services/v1/admin/users/${userId}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      let apiMessage: string | null = null;
      try {
        const body: unknown = await response.json();
        if (
          body &&
          typeof body === 'object' &&
          'message' in body &&
          typeof (body as { message: unknown }).message === 'string'
        ) {
          apiMessage = (body as { message: string }).message;
        }
      } catch {
        // ignore parse errors
      }
      throw new Error(
        apiMessage
          ? `HTTP ${response.status}: ${apiMessage}`
          : `HTTP ${response.status}: ${response.statusText}`
      );
    }

    return true;
  } catch (error) {
    console.error('❌ Error deleting user:', error);
    throw error;
  }
};

/**
 * Assign product to user
 */
export const assignProductToUser = async (
  userId: string | number,
  productId: string | number
): Promise<boolean> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/services/admin/users/${userId}/products`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ productId }),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return true;
  } catch (error) {
    console.error('❌ Error assigning product to user:', error);
    throw error;
  }
};

/**
 * Remove product from user
 */
export const removeProductFromUser = async (
  userId: string | number,
  productId: string | number
): Promise<boolean> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/services/admin/users/${userId}/products/${productId}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return true;
  } catch (error) {
    console.error('❌ Error removing product from user:', error);
    throw error;
  }
};

/**
 * Assign course to user
 */
export const assignCourseToUser = async (
  userId: string | number,
  courseId: string | number
): Promise<boolean> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/services/admin/users/${userId}/courses`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ courseId }),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return true;
  } catch (error) {
    console.error('❌ Error assigning course to user:', error);
    throw error;
  }
};

/**
 * Remove course from user
 */
export const removeCourseFromUser = async (
  userId: string | number,
  courseId: string | number
): Promise<boolean> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/services/admin/users/${userId}/courses/${courseId}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return true;
  } catch (error) {
    console.error('❌ Error removing course from user:', error);
    throw error;
  }
};

/**
 * Logout user from server
 */
export const logoutUser = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return true;
  } catch (error) {
    console.error('❌ Error logging out user:', error);
    throw error;
  }
};

/**
 * Request magic link for authentication
 */
export const requestMagicLink = async (
  email: string
): Promise<{ message: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/magicLinkAsk`, {
      method: 'POST',
      body: JSON.stringify({
        email,
        from: window.location.origin,
        params: window.location.search,
      }),
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('❌ Error requesting magic link:', error);
    throw error;
  }
};

/**
 * Login with email and password
 */
export const specialLogin = async (
  email: string,
  password: string
): Promise<SpecialLoginResponse> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/auth/v1/users/special-login`,
      {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify({
          email,
          password,
        }),
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const responseData: SpecialLoginResponse = await response.json();
    return responseData;
  } catch (error) {
    console.error('❌ Error logging in with special login:', error);
    throw error;
  }
};

/**
 * Fetch admin users list with pagination and search
 */
export const fetchAdminUsers = async (
  page: number = 1,
  pageSize: number = 20,
  query?: string
): Promise<AdminUsersResponse> => {
  try {
    let url = `${API_BASE_URL}/services/v1/admin/users?page=${page}&size=${pageSize}`;
    if (query && query.trim().length > 0) {
      url += `&query=${encodeURIComponent(query.trim())}`;
    }

    const response = await fetch(url, {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();

    // La nuova API restituisce direttamente users e pagination
    return {
      users: Array.isArray(result.users) ? result.users : [],
      pagination: result.pagination || {
        currentPage: page,
        pageSize,
        totalRecords: 0,
        totalPages: 0,
      },
    };
  } catch (error) {
    console.error('❌ Error fetching admin users:', error);
    throw error;
  }
};

/**
 * Fetch the complete admin users directory by iterating all paginated results.
 * This is used by the admin users dashboard to power local analytics and
 * advanced filters across the full dataset.
 */
export const fetchAllAdminUsers = async (
  pageSize: number = 100
): Promise<AdminUsersResponse> => {
  const firstPage = await fetchAdminUsers(1, pageSize);
  const totalPages = Math.max(firstPage.pagination?.totalPages ?? 1, 1);

  if (totalPages === 1) {
    return firstPage;
  }

  const pageRequests: Promise<AdminUsersResponse>[] = [];
  for (let page = 2; page <= totalPages; page += 1) {
    pageRequests.push(fetchAdminUsers(page, pageSize));
  }

  const remainingPages = await Promise.all(pageRequests);
  const users = [
    ...(Array.isArray(firstPage.users) ? firstPage.users : []),
    ...remainingPages.flatMap(result =>
      Array.isArray(result.users) ? result.users : []
    ),
  ];

  return {
    users,
    pagination: {
      currentPage: 1,
      pageSize: users.length || pageSize,
      totalRecords: firstPage.pagination?.totalRecords ?? users.length,
      totalPages,
    },
  };
};

// ============================================================================
// SAFE WRAPPERS (following the pattern of other services)
// ============================================================================

/**
 * Safe wrapper for fetchUserInfo with error handling
 */
export const fetchUserInfoSafe = async (): Promise<UserInfo | null> => {
  try {
    return await fetchUserInfo();
  } catch (error) {
    console.error('❌ Safe fetchUserInfo failed:', error);
    return null;
  }
};

/**
 * Safe wrapper for fetchUserProducts with error handling
 */
export const fetchUserProductsSafe = async (
  userId: string | number
): Promise<UserProduct[]> => {
  try {
    return await fetchUserProducts(userId);
  } catch (error) {
    console.error('❌ Safe fetchUserProducts failed:', error);
    return [];
  }
};

/**
 * Safe wrapper for fetchUserCourses with error handling
 */
export const fetchUserCoursesSafe = async (
  userId: string | number
): Promise<UserCourse[]> => {
  try {
    return await fetchUserCourses(userId);
  } catch (error) {
    console.error('❌ Safe fetchUserCourses failed:', error);
    return [];
  }
};

/**
 * Safe wrapper for updateUserInfo with error handling
 */
export const updateUserInfoSafe = async (
  userId: string | number,
  userData: UpdateUserPayload
): Promise<UserInfo | null> => {
  try {
    return await updateUserInfo(userId, userData);
  } catch (error) {
    console.error('❌ Safe updateUserInfo failed:', error);
    return null;
  }
};

/**
 * Safe wrapper for deleteUser with error handling
 */
export const deleteUserSafe = async (
  userId: string | number
): Promise<boolean> => {
  try {
    return await deleteUser(userId);
  } catch (error) {
    console.error('❌ Safe deleteUser failed:', error);
    return false;
  }
};

/**
 * Safe wrapper for logoutUser with error handling
 */
export const logoutUserSafe = async (): Promise<boolean> => {
  try {
    return await logoutUser();
  } catch (error) {
    console.error('❌ Safe logoutUser failed:', error);
    return false;
  }
};

/**
 * Perform complete logout: invalidate server session, clear local state, stores, and tokens
 * @returns Promise<boolean> - true if server logout succeeded, false otherwise
 */
export const performLogout = async (): Promise<boolean> => {
  try {
    // Try to call the logout endpoint to invalidate server session
    const serverLogoutSuccess = await logoutUserSafe();

    // Always proceed with local logout regardless of API response
    // This ensures the user can logout even if the server is unreachable
    const { useAuthStore } = await import('../store/useAuthStore');
    const { clearAllStores } = await import('../utils/storeCleanup');
    const { clearAblyToken } = await import('../providers/ablyTokenManager');

    // Clear auth store
    useAuthStore.getState().logout();

    // Clear all persisted stores from localStorage
    clearAllStores();

    // Clear Ably token from localStorage
    clearAblyToken();

    return serverLogoutSuccess;
  } catch (error) {
    console.error('Error logging out:', error);

    // Even if the API call fails, proceed with local logout
    const { useAuthStore } = await import('../store/useAuthStore');
    const { clearAllStores } = await import('../utils/storeCleanup');
    const { clearAblyToken } = await import('../providers/ablyTokenManager');

    useAuthStore.getState().logout();
    clearAllStores();
    clearAblyToken();

    return false;
  }
};

/**
 * Safe wrapper for requestMagicLink with error handling
 */
export const requestMagicLinkSafe = async (
  email: string
): Promise<{ message: string } | null> => {
  try {
    return await requestMagicLink(email);
  } catch (error) {
    console.error('❌ Safe requestMagicLink failed:', error);
    throw error; // Re-throw to allow proper error handling in the component
  }
};

/**
 * Safe wrapper for fetchAdminUsers with error handling
 */
export const fetchAdminUsersSafe = async (
  page: number = 1,
  pageSize: number = 20,
  query?: string
): Promise<AdminUsersResponse> => {
  try {
    return await fetchAdminUsers(page, pageSize, query);
  } catch (error) {
    console.error('❌ Safe fetchAdminUsers failed:', error);
    return {
      users: [],
      pagination: {
        currentPage: 1,
        pageSize: 20,
        totalRecords: 0,
        totalPages: 0,
      },
    };
  }
};
